import { ref, computed } from "vue";
import AIAssistantService from "@/services/ai/ai-assistant";

export interface AIBriefing {
  talkingPoints?: string[];
  keyFacts?: string[];
  suggestedOpener?: string;
  [key: string]: any;
}

export interface AISuggestion {
  id: string | number;
  text: string;
  timestamp: number;
}

// ─── Module-level state (shared across components) ───────────────────────────

const isEnabled = ref(false);
const isEnabling = ref(false);
const isPreparing = ref(false);
const isWaitingForCall = ref(false);
const briefing = ref<AIBriefing | null>(null);
const suggestions = ref<AISuggestion[]>([]);
const currentStreamingText = ref("");
const selectedIcpId = ref<string | null>(null);
const isStreaming = ref(false);
const isThinking = ref(false);
const isMinimized = ref(false);
const error = ref<string | null>(null);
const activeCallSid = ref<string | null>(null);

let suggestionIdCounter = 0;
// Bumped on every prepareContext() and resetState(), so a prep that resolves
// after the state was reset (or a newer prep started) can tell it's stale.
let prepareGeneration = 0;

export function useAIAssistant() {
  const hasBriefing = computed(() => !!briefing.value);
  const hasSuggestions = computed(() => suggestions.value.length > 0);
  const latestSuggestion = computed(() =>
    suggestions.value.length > 0 ? suggestions.value[suggestions.value.length - 1] : null
  );

  /**
   * Pre-call context preparation — call this BEFORE makeCall.
   * Fires n8n Workflow 1 with just the contactId, stores the briefing.
   * Resolves with the briefing (or null on failure) after ~25s max.
   */
  const prepareContext = async (
    contactId: string,
    icpId?: string,
    timeoutMs = 25_000
  ): Promise<AIBriefing | null> => {
    if (isPreparing.value) return null;

    const generation = ++prepareGeneration;
    const isStale = () => generation !== prepareGeneration;

    isPreparing.value = true;
    selectedIcpId.value = icpId || null;
    isWaitingForCall.value = false;
    isMinimized.value = false; // Auto-maximize when starting prep
    error.value = null;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await AIAssistantService.prepareContext(contactId, icpId);
      clearTimeout(timeoutId);

      // Belongs to a contact whose call is already over — don't let its
      // briefing leak into whatever is happening now.
      if (isStale()) return null;

      const fetchedBriefing = response.data?.data?.briefing || null;
      if (fetchedBriefing) {
        briefing.value = fetchedBriefing;
      }

      // We are now waiting for the user/system to initiate the call with provideCallSid/enableAssistant
      isWaitingForCall.value = true;

      return fetchedBriefing;
    } catch (err: any) {
      if (isStale()) return null;
      if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
        error.value = err?.response?.data?.msg || err?.message || "Pre-call context prep failed";
        console.error("[AIAssistant] prepareContext failed:", err);
      }
      return null;
    } finally {
      if (!isStale()) isPreparing.value = false;
    }
  };

  /**
   * Enable AI assistant for a call. Calls backend, which fires Workflow 1.
   * `icpId` overrides the one remembered from prepareContext(), which is lost
   * if the state gets reset between prep and the call connecting.
   */
  const enableAssistant = async (
    callSid: string,
    contactId: string,
    parentCallSid?: string,
    icpId?: string
  ): Promise<void> => {
    if (isEnabled.value || isEnabling.value) return;

    isEnabling.value = true;
    isWaitingForCall.value = false; // Transitioning from waiting to enabling
    error.value = null;
    const sessionSid = parentCallSid || callSid;
    activeCallSid.value = sessionSid;
    // Enabling can take a while (context prep). If the call ended and state
    // was reset meanwhile, the response belongs to a finished call.
    const isStale = () => activeCallSid.value !== sessionSid;

    try {
      const response = await AIAssistantService.enable({
        callSid,
        parentCallSid,
        contactId,
        icpId: icpId || selectedIcpId.value || undefined,
      });

      if (isStale()) {
        // Stop the backend session that was just started for the ended call.
        AIAssistantService.disable(sessionSid).catch(() => {});
        return;
      }

      isEnabled.value = true;

      // Briefing may come via the HTTP response or via socket
      if (response.data?.data?.briefing) {
        briefing.value = response.data.data.briefing;
      }
    } catch (err: any) {
      if (isStale()) return;
      error.value = err?.response?.data?.msg || err?.message || "Failed to enable AI assistant";
      console.error("[AIAssistant] Enable failed:", err);
    } finally {
      if (!isStale()) isEnabling.value = false;
    }
  };

  /**
   * Disable AI assistant for the current call.
   */
  const disableAssistant = async (): Promise<void> => {
    try {
      if (isEnabled.value && activeCallSid.value) {
        await AIAssistantService.disable(activeCallSid.value);
      }
    } catch (err: any) {
      console.error("[AIAssistant] Disable failed:", err);
    } finally {
      resetState();
    }
  };

  /**
   * Handle socket event: call:ai-assist:briefing
   */
  const handleBriefing = (data: { callSid: string; briefing: any }) => {
    if (activeCallSid.value && data.callSid !== activeCallSid.value) return;
    briefing.value = data.briefing;
    isEnabling.value = false;
  };

  /**
   * Handle socket event: call:ai-assist:thinking
   */
  const handleThinking = (data: { callSid: string }) => {
    if (activeCallSid.value && data.callSid !== activeCallSid.value) return;
    isThinking.value = true;
    isStreaming.value = false;
    currentStreamingText.value = "";
  };

  /**
   * Handle socket event: call:ai-assist:suggestion
   */
  const handleSuggestion = (data: {
    callSid: string;
    text: string;
    done: boolean;
    error?: string;
    suggestionId?: string;
  }) => {
    if (activeCallSid.value && data.callSid !== activeCallSid.value) return;

    if (data.error) {
      error.value = data.error;
      isThinking.value = false;
      isStreaming.value = false;
      return;
    }

    if (!data.done) {
      isThinking.value = false;
      isStreaming.value = true;
      currentStreamingText.value += data.text;
    } else {
      isStreaming.value = false;
      isThinking.value = false;

      const finalText = currentStreamingText.value || data.text;
      if (finalText && finalText.trim()) {
        // prefer backend suggestionId; counter is fallback for older backend versions
        const id = data.suggestionId ? data.suggestionId : String(++suggestionIdCounter);
        // dedupe: reconnect can deliver the same suggestion twice
        if (!suggestions.value.some((s) => s.id === id)) {
          suggestions.value.push({ id, text: finalText.trim(), timestamp: Date.now() });
          if (suggestions.value.length > 30) {
            suggestions.value = suggestions.value.slice(-30);
          }
        }
      }

      currentStreamingText.value = "";
    }
  };

  /**
   * Reset all state when call ends or assistant is disabled.
   */
  const resetState = () => {
    isEnabled.value = false;
    isEnabling.value = false;
    isPreparing.value = false;
    isWaitingForCall.value = false;
    briefing.value = null;
    suggestions.value = [];
    currentStreamingText.value = "";
    isStreaming.value = false;
    isThinking.value = false;
    isMinimized.value = false;
    error.value = null;
    activeCallSid.value = null;
    selectedIcpId.value = null;
    suggestionIdCounter = 0;
    prepareGeneration++;
  };

  const minimize = () => {
    isMinimized.value = true;
  };

  const maximize = () => {
    isMinimized.value = false;
  };

  return {
    // State
    isEnabled,
    isEnabling,
    isPreparing,
    isWaitingForCall,
    briefing,
    suggestions,
    currentStreamingText,
    isStreaming,
    isThinking,
    isMinimized,
    error,
    activeCallSid,

    // Computed
    hasBriefing,
    hasSuggestions,
    latestSuggestion,

    // Actions
    prepareContext,
    enableAssistant,
    disableAssistant,
    minimize,
    maximize,
    resetState,

    // Socket handlers (to be wired in App.vue)
    handleBriefing,
    handleThinking,
    handleSuggestion,
  };
}
